import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Archive } from 'lucide-react';
import { api } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import { Card, CardContent } from './ui/card';

import RecouvrementHeader from './recouvrement/RecouvrementHeader';
import RecouvrementStatsCards from './recouvrement/RecouvrementStatsCards';
import ClassesConcerneesSection from './recouvrement/ClassesConcerneesSection';
import DossiersSection from './recouvrement/DossiersSection';
import { matchRecherche } from './recouvrement/recouvrementUtils';

const ITEMS_PER_PAGE = 10;

const RecouvrementModule = () => {
  const isArchivedYear = localStorage.getItem('annee_scolaire_read_only') === 'true';
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Sections dépliables, comme MaCaisse
  const [expandedSections, setExpandedSections] = useState(new Set(['classes', 'dossiers']));

  const toggleSection = (sectionName) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionName)) newSet.delete(sectionName); else newSet.add(sectionName);
      return newSet;
    });
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
  }, []);

  const loadData = async ({ silent = false } = {}) => {
    if (isArchivedYear) {
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);

    try {
      const response = await api.anneesScolaires.getRecouvrementDerniereAnneeCloturee();
      if (!response?.success) {
        throw new Error(response?.message || 'Impossible de charger le recouvrement');
      }
      setData(response.data);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement du recouvrement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const synthese = data?.synthese || {};
  const eleves = data?.eleves || [];
  const classes = data?.repartition_par_classe || [];

  const filteredEleves = useMemo(
    () => eleves.filter((eleve) => matchRecherche(eleve, search)),
    [eleves, search]
  );

  const totalPages = Math.max(1, Math.ceil(filteredEleves.length / ITEMS_PER_PAGE));

  // ============================================================================
  // ANNÉE ARCHIVÉE
  // ============================================================================
  if (isArchivedYear) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-6">
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Archive size={22} className="text-amber-600" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Année archivée</p>
                <h1 className="mt-1 text-2xl font-bold text-amber-900">
                  Recouvrement non disponible depuis cette année
                </h1>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-amber-800">
                  Le recouvrement des dettes d'une année clôturée se traite depuis la nouvelle année active.
                  Cette vue archivée sert uniquement à consulter les données historiques de l'année sélectionnée.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // CHARGEMENT INITIAL
  // ============================================================================
  if (loading) {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-6">
        <div className="flex min-h-[360px] items-center justify-center">
          <LoadingSpinner message="Chargement du recouvrement..." color="#246990" />
        </div>
      </div>
    );
  }

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="w-full min-h-screen bg-gray-50">
      <div className="w-full space-y-6">

        <RecouvrementHeader
          currentUser={currentUser}
          anneeCreance={data?.annee}
          anneeActive={data?.annee_active}
          elevesConcernes={Number(synthese.eleves_concernes || 0)}
          resteARecouvrer={Number(synthese.reste_a_recouvrer || 0)}
          onRefresh={() => loadData({ silent: true })}
          refreshing={refreshing}
        />

        {error && (
          <div className="px-6">
            <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="font-semibold text-red-900">Recouvrement indisponible</h5>
                  <p className="mt-1 text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* L'onglet n'existe que s'il y a une année clôturée (GestionComptable,
            route /annees-scolaires/recouvrement/disponible) : plus de message
            « aucune année à recouvrer », le module ne s'affiche simplement pas. */}
        {!data?.annee ? null : (
          <>
            <div className="relative">
              {refreshing && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70">
                  <LoadingSpinner />
                </div>
              )}

              <RecouvrementStatsCards
                synthese={synthese}
                classes={classes}
                eleves={eleves}
                isLoading={refreshing}
              />
            </div>

            <ClassesConcerneesSection
              isExpanded={expandedSections.has('classes')}
              onToggleSection={() => toggleSection('classes')}
              classes={classes}
            />

            <DossiersSection
              isExpanded={expandedSections.has('dossiers')}
              onToggleSection={() => toggleSection('dossiers')}
              eleves={eleves}
              filteredEleves={filteredEleves}
              search={search}
              setSearch={setSearch}
              currentPage={Math.min(currentPage, totalPages)}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default RecouvrementModule;
